/**
 * The Find Readers receipt, at the top of the analytics readers page.
 *
 * Pressing "Find Readers" on a work card sends the author here, and this box is the reason why:
 * it is the only confirmation they get that a batch went out. It reports one dispatch — how many
 * readers, how long ago, which paper — and disappears when the sender cooldown expires, which is
 * the same moment the countdown on the second line reaches zero.
 *
 * Renders nothing at all when the API has nothing to report, so the page is unchanged for the
 * overwhelming majority of visits.
 */
import React, { useEffect, useState } from 'react';
import routes from 'js/core_pre_webpack/routes';

type Report = {
  recipientCount: number;
  sentAgo: string;
  availableIn: string;
  workTitle: string;
};

// Set by js/profiles/setUpFindReaders just before it navigates here, when the dispatch matched
// nobody. That run writes no rows, so there is nothing for the API to report and the page would
// otherwise look exactly like a successful send.
//
// Read once and deleted, which is what makes the message a one-off: it belongs to the press that
// just happened, not to the page, and a reload should not repeat it.
const NO_CANDIDATES_KEY = 'findReaders:noCandidates';

const takeEmptyRunFlag = (): boolean => {
  try {
    const flagged = window.sessionStorage.getItem(NO_CANDIDATES_KEY) != null;
    window.sessionStorage.removeItem(NO_CANDIDATES_KEY);
    return flagged;
  } catch {
    // Private mode, or storage disabled.
    return false;
  }
};

// The API answers `{}` when there is no batch inside the window, so presence of the count is what
// distinguishes "nothing to report" from a report of nothing.
const isReport = (value: unknown): value is Report =>
  value != null &&
  typeof (value as Report).recipientCount === 'number' &&
  (value as Report).recipientCount > 0;

export const FindReadersReport = (): JSX.Element | null => {
  const [report, setReport] = useState<Report | null>(null);
  // Read during the first render rather than in the effect, so the flag is consumed even if the
  // component is torn down before the effect runs — it belongs to one arrival either way.
  const [emptyRun] = useState(takeEmptyRunFlag);

  useEffect(() => {
    // Nothing to fetch: a run that matched nobody wrote no rows, and it can only happen outside
    // the cooldown, so there is no older batch sitting behind it either.
    if (emptyRun) return undefined;

    let current = true;

    fetch(routes.api_v0_find_readers_report_path({ subdomain_param: 'api' }), {
      credentials: 'same-origin',
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: unknown) => {
        if (current && isReport(data)) setReport(data);
      })
      // The box is supplementary; the readers list behind it is the page. A failure here should
      // cost the author nothing.
      .catch(() => {});

    return () => {
      current = false;
    };
  }, [emptyRun]);

  if (emptyRun) {
    return (
      <div className="find-readers-report">
        <p className="find-readers-report__headline">
          Sorry, we could not find readers for your paper at this time. You can
          try a different paper, or try again later.
        </p>
      </div>
    );
  }

  if (report == null) return null;

  return (
    <div className="find-readers-report">
      {/*
        "invited", never "sent an email blast": these emails go out in the author's OWN name, and
        the rest of the feature — the landing page, the analytics column explainer, the button's
        tooltip — all say invite. A receipt in marketing vocabulary tells an academic they just
        spammed a hundred strangers under their own byline, which is the exact worry
        FindReaders::LandingPageVisitors exists to respect.
      */}
      <p className="find-readers-report__headline">
        You invited{' '}
        <strong>
          {report.recipientCount}{' '}
          {report.recipientCount === 1 ? 'academic' : 'academics'}
        </strong>{' '}
        to read your paper &ldquo;<strong>{report.workTitle}</strong>&rdquo;,{' '}
        {report.sentAgo}.
      </p>
      <p className="find-readers-report__note">
        You can do this again in <strong>{report.availableIn}</strong>.
      </p>
    </div>
  );
};

export default FindReadersReport;
